import React, { useState, useMemo, useCallback, useEffect } from 'react';
// ZMĚNA: Přidán import ikony FaUnlink
import { FaTrash, FaPlusCircle, FaPencilAlt, FaSave, FaUnlink } from 'react-icons/fa';
import './AddonTextsEditor.css';

const QuoteTextItem = ({ textItem, onUpdateText, onRemoveText }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editText, setEditText] = useState(textItem.Text_Content || '');

    const handleSave = () => {
        onUpdateText(textItem.id, editText);
        setIsEditing(false);
    };

    useEffect(() => {
        setEditText(textItem.Text_Content || '');
    }, [textItem.Text_Content]);

    // ZMĚNA: Struktura JSX upravena pro flexbox layout
    return (
        <div className={`text-item ${isEditing ? 'editing' : ''}`}>
            <div className="text-item-content">
                {textItem.Header && <strong>{textItem.Header}</strong>}
                {isEditing ? (
                    <textarea 
                        value={editText} 
                        onChange={(e) => setEditText(e.target.value)} 
                        rows="6" // Zvětšený počet řádků pro lepší editaci
                        autoFocus 
                        onBlur={handleSave} 
                    />
                ) : (
                    <p>{textItem.Text_Content}</p>
                )}
            </div>
            <div className="item-actions">
                {/* ZMĚNA: Přidána ikonka pro "odpojený" text */}
                {textItem.Is_Detached && (
                    <span className="action-btn detached-icon" title="Text byl upraven a odpojen od šablony">
                        <FaUnlink />
                    </span>
                )}
                {isEditing ? (
                    <button onClick={handleSave} className="action-btn save-btn" title="Uložit změny"><FaSave /></button>
                ) : (
                    <button onClick={() => setIsEditing(true)} className="action-btn edit-btn" title="Upravit text"><FaPencilAlt /></button>
                )}
                <button onClick={() => onRemoveText(textItem.id)} className="action-btn delete-btn" title="Odebrat tento text"><FaTrash /></button>
            </div>
        </div>
    );
};

const TextGroup = ({ groupTemplate, allTextTemplates, quoteTexts, onAddText, onUpdateText, onRemoveText, preferredLanguage }) => {
    const [isAddMenuOpen, setIsAddMenuOpen] = useState(false);

    const availableTextsToAdd = useMemo(() => {
        const textsForThisGroup = allTextTemplates.filter(t => t.parentGroupName === groupTemplate.name);
        const quoteTextTemplateIds = new Set(quoteTexts.map(t => t.Source_Template?.id));
        return textsForThisGroup.filter(templateText => !quoteTextTemplateIds.has(templateText.id));
    }, [allTextTemplates, groupTemplate.name, quoteTexts]);

    const handleMenuToggle = (e) => {
        e.stopPropagation();
        setIsAddMenuOpen(!isAddMenuOpen);
    };
    
    useEffect(() => {
        const listener = () => setIsAddMenuOpen(false);
        window.addEventListener('click', listener);
        return () => window.removeEventListener('click', listener);
    }, []);

    const groupDisplayName = groupTemplate.header?.[preferredLanguage] || groupTemplate.header?.en || groupTemplate.name;

    return (
        <div className="text-group">
            <div className="group-header">
                <h3>{groupDisplayName}</h3>
                <div className="group-actions">
                    {availableTextsToAdd.length > 0 && (
                        <div className="add-text-container">
                             <button onClick={handleMenuToggle} className="add-text-btn" title="Přidat text do této skupiny"><FaPlusCircle /></button>
                            {isAddMenuOpen && (
                                <div className="add-text-menu" onClick={(e) => e.stopPropagation()}>
                                    {availableTextsToAdd.map(textToAdd => {
                                        const headerText = textToAdd.header?.[preferredLanguage] || textToAdd.header?.en || textToAdd.name;
                                        return (
                                            <div key={textToAdd.id} className="add-text-menu-item" onClick={() => { onAddText(textToAdd, groupTemplate); setIsAddMenuOpen(false); }}>
                                                {headerText}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="text-items-container">
                {quoteTexts.map(textItem => (
                     <QuoteTextItem key={textItem.id} textItem={textItem} onUpdateText={onUpdateText} onRemoveText={onRemoveText} />
                ))}
            </div>
        </div>
    );
};

const AddonTextsEditor = ({ texts, onTextsChange, templates, preferredLanguage }) => {

    const { groups: groupTemplates, texts: allTextTemplates } = useMemo(() => {
        const t = templates || { groups: [], texts: [] };
        return t;
    }, [templates]);

    const textsWithIds = useMemo(() => texts.map((text, index) => ({
        ...text,
        id: text.id || `local_${index}_${Date.now()}`
    })), [texts]);
    
    const handleUpdateText = (textId, newContent) => {
        const newTexts = textsWithIds.map(t =>
            t.id === textId ? { ...t, Text_Content: newContent, Is_Detached: true } : t
        );
        onTextsChange(newTexts);
    };
    
    const handleRemoveText = (textIdToRemove) => {
        if (window.confirm('Opravdu chcete odebrat tento text z nabídky?')) {
            const newTexts = textsWithIds.filter(t => t.id !== textIdToRemove);
            onTextsChange(newTexts);
        }
    };
    
    const handleAddText = useCallback((textTemplate, groupTemplate) => {
        const newText = {
            id: `new_${textTemplate.id}_${Date.now()}`,
            Source_Template: { id: textTemplate.id, name: textTemplate.name },
            Header: textTemplate.header?.[preferredLanguage] || textTemplate.header?.en || '',
            Text_Content: textTemplate.content?.[preferredLanguage] || textTemplate.content?.en || '',
            Group_Name: groupTemplate.name,
            Placement: groupTemplate.placement,
            Sequence: textTemplate.sequence,
            Is_Detached: false,
        };
        onTextsChange([...textsWithIds, newText]);
    }, [textsWithIds, onTextsChange, preferredLanguage]);

    const renderPlacementSection = (placement) => {
        const groupsForPlacement = groupTemplates
            .filter(g => g.placement === placement)
            .sort((a,b) => (a.sequence || 0) - (b.sequence || 0));
        
        if (groupsForPlacement.length === 0) return null;

        return (
            <div className="placement-section">
                <h4>{placement === 'Above Items' ? 'Texty nad položkami' : 'Texty pod položkami'}</h4>
                {groupsForPlacement.map(groupTemplate => {
                    const textsForThisGroup = textsWithIds.filter(
                        t => t.Group_Name === groupTemplate.name
                    ).sort((a,b) => (a.Sequence || 0) - (b.Sequence || 0));

                    return (
                        <TextGroup
                            key={groupTemplate.id}
                            groupTemplate={groupTemplate}
                            allTextTemplates={allTextTemplates}
                            quoteTexts={textsForThisGroup}
                            onAddText={handleAddText}
                            onUpdateText={handleUpdateText}
                            onRemoveText={handleRemoveText}
                            preferredLanguage={preferredLanguage}
                        />
                    );
                })}
            </div>
        );
    };

    return (
        <div className="addon-texts-editor">
            {renderPlacementSection('Above Items')}
            {renderPlacementSection('Below Items')}
        </div>
    );
};

export default AddonTextsEditor;